/**
 * Reusable Share Sheet — a bottom-sheet modal showing the share banner
 * preview + multi-platform share buttons (WhatsApp, Instagram, Facebook,
 * X/Twitter, Telegram, LinkedIn, Email, Copy link, and native share).
 *
 * Props:
 *   open           : boolean
 *   onClose        : () => void
 *   payload        : { kind, headline, subline, text, link, banner_url, targets[] }
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   const [payload, setPayload] = useState(null);
 *   ...
 *   <ShareSheet open={open} onClose={()=>setOpen(false)} payload={payload} />
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Share2, Copy, Download, MessageCircle, Send,
  Instagram, Facebook, Linkedin, Mail, Twitter, Link as LinkIcon,
} from 'lucide-react';

const ICONS = {
  whatsapp: MessageCircle,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  telegram: Send,
  linkedin: Linkedin,
  email: Mail,
  copy: LinkIcon,
};

const COLORS = {
  whatsapp: '#25D366',
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#0F1419',
  telegram: '#229ED9',
  linkedin: '#0A66C2',
  email: '#475569',
  copy: '#4F46E5',
};

async function dataUrlFromUrl(url) {
  const res = await fetch(url, { mode: 'cors' });
  const blob = await res.blob();
  return blob;
}

export default function ShareSheet({ open, onClose, payload }) {
  const sheetRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open || !payload) return null;

  const downloadBanner = async () => {
    try {
      setDownloading(true);
      const blob = await dataUrlFromUrl(payload.banner_url);
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `becaim-${payload.kind}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      toast.success('Banner downloaded — open Instagram and share to your Story!');
    } catch (e) {
      toast.error('Could not download banner');
    } finally {
      setDownloading(false);
    }
  };

  const copyLink = async () => {
    const text = `${payload.text}\n${payload.link}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('no-clipboard-api');
      }
      toast.success('Copied to clipboard');
    } catch {
      // execCommand fallback for older browsers / iframes where clipboard API is blocked
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) toast.success('Copied to clipboard');
        else toast.error('Copy failed');
      } catch {
        toast.error('Copy failed');
      }
    }
  };

  const nativeShare = async () => {
    // Try Web Share Level 2 (with file) for mobile users — opens system sheet
    // including Instagram, Snapchat, etc.
    try {
      if (navigator.canShare && navigator.share) {
        // Best path: share the actual image file
        try {
          const blob = await dataUrlFromUrl(payload.banner_url);
          const file = new File([blob], `becaim-${payload.kind}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: 'BecAim AI',
              text: payload.text,
              url: payload.link,
            });
            return;
          }
        } catch (e) { /* fall through to text-only share */ }
        await navigator.share({
          title: 'BecAim AI',
          text: payload.text,
          url: payload.link,
        });
      } else {
        await copyLink();
      }
    } catch (e) {
      // User cancelled — silent
    }
  };

  const onTargetClick = async (target) => {
    if (target.id === 'copy') {
      await copyLink();
      return;
    }
    if (target.id === 'instagram') {
      await downloadBanner();
      return;
    }
    // Open the platform's share URL in a new tab
    window.open(target.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose} data-testid="share-sheet">
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-5 max-h-[92vh] overflow-y-auto gm-slide-up"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold flex items-center gap-2">
            <Share2 size={20} strokeWidth={2.6} color="#4F46E5" /> Share
          </h3>
          <button onClick={onClose} className="p-1" data-testid="share-sheet-close"><X size={22} strokeWidth={2.6} /></button>
        </div>

        {/* Banner preview */}
        <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm mb-4">
          <img
            src={payload.banner_url}
            alt="Share banner"
            className="w-full h-auto block"
            data-testid="share-banner-preview"
          />
        </div>

        {/* Caption */}
        <div className="bg-[#F4F6FB] rounded-2xl p-3 text-sm font-semibold text-[#475569] mb-4" data-testid="share-text">
          {payload.text}
          <div className="text-xs font-bold text-[#4F46E5] mt-1 break-all">{payload.link}</div>
        </div>

        {/* Native share + download */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button onClick={nativeShare} className="gm-btn gm-btn-primary py-3" data-testid="share-native">
            <Share2 size={18} strokeWidth={2.6} /> Share
          </button>
          <button onClick={downloadBanner} disabled={downloading} className="gm-btn gm-btn-secondary py-3" data-testid="share-download">
            <Download size={18} strokeWidth={2.6} /> {downloading ? '…' : 'Save image'}
          </button>
        </div>

        {/* Platforms */}
        <div className="grid grid-cols-4 gap-3" data-testid="share-targets">
          {payload.targets?.map((t) => {
            const Icon = ICONS[t.id] || Share2;
            return (
              <button
                key={t.id}
                onClick={() => onTargetClick(t)}
                className="flex flex-col items-center gap-1.5 py-2 group"
                data-testid={`share-${t.id}`}
              >
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-active:scale-95"
                  style={{ background: COLORS[t.id] || '#4F46E5' }}
                >
                  {t.id === 'copy' ? <Copy size={22} color="white" strokeWidth={2.6} /> : <Icon size={22} color="white" strokeWidth={2.6} />}
                </span>
                <span className="text-[11px] font-bold text-[#475569] text-center leading-tight">{t.label}</span>
              </button>
            );
          })}
        </div>

        {payload.targets?.some((t) => t.id === 'instagram') && (
          <p className="text-[11px] font-semibold text-[#94A3B8] mt-3 text-center">
            Tip for Instagram: tap <b>Save image</b>, then open Insta → New Story → pick from gallery.
          </p>
        )}
      </div>
    </div>
  );
}
